import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { API_URL, formatCurrency, formatDate, getStatusColor, statusLabels } from "../../lib/utils";
import { FileText, CheckCircle, XCircle, Loader2 } from "lucide-react";

export const PortalQuotesPage = () => {
    const { token } = useAuth();
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [respondDialog, setRespondDialog] = useState(null); // {quote, action}
    const [submitting, setSubmitting] = useState(false);

    const fetchQuotes = async () => {
        try {
            const res = await fetch(`${API_URL}/portal/quotes`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) setQuotes(await res.json());
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchQuotes(); }, [token]);

    const handleRespond = async () => {
        if (!respondDialog) return;
        setSubmitting(true);
        try {
            const res = await fetch(
                `${API_URL}/portal/quotes/${respondDialog.quote.quote_id}/respond?action=${respondDialog.action}`,
                {
                    method: "PUT",
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Erreur");
            }
            toast.success(
                respondDialog.action === "accepted" ? "Devis accepté" : "Devis refusé"
            );
            setRespondDialog(null);
            fetchQuotes();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="portal-quotes-page">
            <div>
                <h1 className="font-heading font-bold text-2xl text-foreground">Mes devis</h1>
                <p className="text-muted-foreground text-sm mt-1">Consultez et répondez à vos devis</p>
            </div>

            {quotes.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">Aucun devis pour le moment</p>
                    </CardContent>
                </Card>
            ) : (
                <Card>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>N° Devis</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Montant</TableHead>
                                <TableHead>Statut</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {quotes.map((q) => (
                                <TableRow key={q.quote_id}>
                                    <TableCell className="font-medium">{q.quote_number}</TableCell>
                                    <TableCell>{formatDate(q.created_at)}</TableCell>
                                    <TableCell className="text-right font-mono">{formatCurrency(q.total)}</TableCell>
                                    <TableCell>
                                        <Badge className={getStatusColor(q.status)}>
                                            {statusLabels[q.status] || q.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        {q.status === "sent" && (
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={() => setRespondDialog({ quote: q, action: "accepted" })}
                                                    data-testid={`accept-quote-${q.quote_id}`}
                                                >
                                                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                                    Accepter
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setRespondDialog({ quote: q, action: "refused" })}
                                                    data-testid={`refuse-quote-${q.quote_id}`}
                                                >
                                                    <XCircle className="w-3.5 h-3.5 mr-1" />
                                                    Refuser
                                                </Button>
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Card>
            )}

            {/* Confirm Dialog */}
            <Dialog open={!!respondDialog} onOpenChange={() => setRespondDialog(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {respondDialog?.action === "accepted" ? "Accepter le devis" : "Refuser le devis"}
                        </DialogTitle>
                        <DialogDescription>
                            {respondDialog?.action === "accepted"
                                ? `Vous confirmez l'acceptation du devis ${respondDialog?.quote.quote_number} pour un montant de ${formatCurrency(respondDialog?.quote.total)} ?`
                                : `Vous confirmez le refus du devis ${respondDialog?.quote.quote_number} ?`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRespondDialog(null)}>
                            Annuler
                        </Button>
                        <Button
                            onClick={handleRespond}
                            disabled={submitting}
                            variant={respondDialog?.action === "refused" ? "destructive" : "default"}
                            data-testid="confirm-respond-btn"
                        >
                            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {respondDialog?.action === "accepted" ? "Confirmer l'acceptation" : "Confirmer le refus"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
